import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, Field, Skeleton, StatusBadge } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { countryName } from '../../lib/business.ts';
import { loadTaxRates, ratePct, useTaxRates, type TaxKind, type TaxRate } from '../../lib/taxes.ts';
import { ManageLayout, useSave } from './common.tsx';

const KINDS: { kind: TaxKind; label: MessageKey; hint: MessageKey }[] = [
  { kind: 'standard', label: 'tax.kind_standard', hint: 'tax.kind_standard_hint' },
  { kind: 'reduced', label: 'tax.kind_reduced', hint: 'tax.kind_reduced_hint' },
  { kind: 'zero', label: 'tax.kind_zero', hint: 'tax.kind_zero_hint' },
  { kind: 'exempt', label: 'tax.kind_exempt', hint: 'tax.kind_exempt_hint' },
];
const kindLabel = (k: TaxKind) => t(KINDS.find((x) => x.kind === k)!.label);

/** What the person typed as a rate ("5,5" or "5.5"), or null. */
export function parsePercent(text: string): number | null {
  const clean = text.trim().replace(/\s|%/g, '').replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(clean)) return null;
  const n = Number(clean);
  return n <= 100 ? n : null;
}

/**
 * Settings → Tax rates: the rates products, invoices and quotes may use. A new business gets its
 * country's usual VAT rates as a start, to be checked with its accountant. A rate never changes
 * once made: when the law changes, the new one is added and the old one turned off, so what
 * was sold before keeps its rate.
 */
export function TaxRates() {
  const { all, rules, loaded } = useTaxRates();
  const [editing, setEditing] = useState<TaxRate | 'new' | null>(null);
  const { run } = useSave();
  const on = all.filter((r) => r.active);
  const off = all.filter((r) => !r.active);

  const toggle = (r: TaxRate) =>
    run(async () => {
      await action('company.save_tax_rate', { id: r.id, name: r.name, rate: r.rate, kind: r.kind, active: !r.active });
      await loadTaxRates(true);
    }, r.active ? t('tax.turned_off_done', { name: r.name }) : t('tax.turned_on_done', { name: r.name }));

  const line = (r: TaxRate) => (
    <div role="listitem" key={r.id} className={`list-row${r.active ? '' : ' archived'}`}>
      <span className="tax-pct tabular" aria-hidden="true">
        {ratePct(r.rate)}
      </span>
      <button className="list-main" onClick={() => setEditing(r)}>
        <span className="list-title">{r.name}</span>
        <span className="list-sub">{kindLabel(r.kind)}</span>
      </button>
      {r.active ? null : <StatusBadge tone="neutral" icon="pause" label={t('tax.off')} />}
      <Button size="sm" variant="secondary" icon={r.active ? 'pause' : 'play'} onClick={() => void toggle(r)}>
        {r.active ? t('tax.turn_off') : t('tax.turn_on')}
      </Button>
    </div>
  );

  return (
    <ManageLayout
      title={t('manage.taxes')}
      subtitle={rules ? t(rules.tax === 'sales_tax' ? 'tax.subtitle_sales' : 'tax.subtitle_vat', { country: countryName(rules.code) }) : undefined}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')} disabled={!loaded}>
          {t('tax.add')}
        </Button>
      }
    >
      {!loaded ? (
        <Skeleton h={220} r="var(--radius-lg)" />
      ) : (
        <>
          <div className="callout callout-info">
            <Icon name="info" size={18} />
            <span>{rules?.tax === 'sales_tax' ? t('tax.check_sales') : t('tax.check_vat')}</span>
          </div>
          <div className="list-card" role="list" aria-label={t('tax.in_use')}>
            {on.map(line)}
          </div>
          {off.length ? (
            <>
              <h2 className="section-title">{t('tax.not_in_use')}</h2>
              <div className="list-card" role="list" aria-label={t('tax.not_in_use')}>
                {off.map(line)}
              </div>
            </>
          ) : null}
          <p className="muted fx-note">
            <Icon name="lock" size={14} /> {t('tax.how')}
          </p>
        </>
      )}
      {editing ? <RateDialog rate={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </ManageLayout>
  );
}

function RateDialog({ rate, onClose }: { rate: TaxRate | null; onClose: () => void }) {
  const [name, setName] = useState(rate?.name ?? '');
  const [pct, setPct] = useState(rate ? String(rate.rate) : '');
  const [kind, setKind] = useState<TaxKind>(rate?.kind ?? 'reduced');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const value = parsePercent(pct);
  const nameErr = !name.trim() ? t('form.required') : null;
  const pctErr = value === null ? t('tax.rate_rule') : null;

  const save = async () => {
    setTried(true);
    if (nameErr || pctErr) return;
    const ok = await run(async () => {
      await action('company.save_tax_rate', { ...(rate ? { id: rate.id, active: rate.active } : {}), name: name.trim(), rate: value, kind });
      await loadTaxRates(true);
    }, t('tax.saved'));
    if (ok) onClose();
  };

  return (
    <Dialog
      title={rate ? t('tax.edit_title') : t('tax.add_title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label={t('tax.name')} hint={t('tax.name_hint')} error={tried ? nameErr : null} required>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('tax.rate')} hint={rate ? t('tax.rate_fixed') : t('tax.rate_hint')} error={tried ? pctErr : null} required>
          <span className="input-unit">
            <input className="input tabular" inputMode="decimal" value={pct} disabled={!!rate} onChange={(e) => setPct(e.target.value)} />
            <span>%</span>
          </span>
        </Field>
        <Field label={t('tax.kind')} hint={t(KINDS.find((k) => k.kind === kind)!.hint)}>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as TaxKind)}>
            {KINDS.map((k) => (
              <option key={k.kind} value={k.kind}>
                {t(k.label)}
              </option>
            ))}
          </select>
        </Field>
      </form>
    </Dialog>
  );
}
